import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { Process } from '@/types';

const schema = z.object({
  name: z.string().min(2, 'Minimo 2 caracteres'),
  code: z.string().optional(),
  description: z.string().optional(),
  estimated_hours: z.number().min(0).optional(),
  sequence_order: z.number().min(0).optional(),
  requires_machine: z.boolean().default(false),
  requires_material: z.boolean().default(false),
  is_active: z.boolean().default(true),
});
type FormData = z.infer<typeof schema>;

interface Props { open: boolean; onOpenChange: (o: boolean) => void; process?: Process | null; onSaved?: () => void; }

function FI({ label, name, required, reg, err, touched, type = 'text', ph }: any) {
  const e = err[name]; const ok = touched[name] && !e;
  return (<div className="space-y-1.5"><Label className={cn(e && 'text-destructive')}>{label}{required && <span className="text-destructive ml-1">*</span>}</Label><div className="relative"><Input type={type} placeholder={ph} {...reg(name, { valueAsNumber: type === 'number' })} className={cn(e && 'border-destructive', ok && 'border-green-500')} />{ok && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}</div>{e && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" />{e.message}</p>}</div>);
}

export function ProcessFormDialog({ open, onOpenChange, process, onSaved }: Props) {
  const isEdit = !!process;
  const qc = useQueryClient();
  const { register, handleSubmit, setValue, watch, reset, formState: { errors, touchedFields, isSubmitting, isValid, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema), mode: 'onBlur',
    defaultValues: { name: '', code: '', description: '', estimated_hours: 0, sequence_order: 0, requires_machine: false, requires_material: false, is_active: true },
  });

  useEffect(() => {
    if (!open) return;
    if (process) {
      reset({ name: process.name || '', code: process.code || '', description: process.description || '', estimated_hours: process.estimated_hours || 0, sequence_order: process.sequence_order || 0, requires_machine: process.requires_machine || false, requires_material: process.requires_material || false, is_active: process.is_active ?? true });
    } else {
      reset({ name: '', code: '', description: '', estimated_hours: 0, sequence_order: 0, requires_machine: false, requires_material: false, is_active: true });
    }
  }, [open, process, reset]);

  const save = useMutation({
    mutationFn: async (d: FormData) => {
      const p = { name: d.name.trim(), code: d.code?.trim() || null, description: d.description?.trim() || null, estimated_hours: d.estimated_hours || null, sequence_order: d.sequence_order || 0, requires_machine: d.requires_machine, requires_material: d.requires_material, is_active: d.is_active };
      if (isEdit && process) { const { error } = await api.from('manufacturing_processes').update(p).eq('id', process.id); if (error) throw error; }
      else { const { error } = await api.from('manufacturing_processes').insert(p); if (error) throw error; }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['processes'] }); toast.success(isEdit ? 'Proceso actualizado' : 'Proceso creado'); onOpenChange(false); onSaved?.(); },
    onError: (e: any) => toast.error(e.message || 'Error'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar Proceso' : 'Nuevo Proceso'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
          <FI label="Nombre" name="name" required reg={register} err={errors} touched={touchedFields} />
          <FI label="Codigo" name="code" reg={register} err={errors} touched={touchedFields} />
          <div className="grid grid-cols-2 gap-4">
            <FI label="Horas Est." name="estimated_hours" type="number" reg={register} err={errors} touched={touchedFields} />
            <FI label="Orden" name="sequence_order" type="number" reg={register} err={errors} touched={touchedFields} />
          </div>
          <div className="space-y-1.5"><Label>Descripcion</Label><Textarea {...register('description')} rows={2} /></div>
          <div className="space-y-3">
            <div className="flex items-center justify-between"><Label>Requiere Maquina</Label><Switch checked={watch('requires_machine')} onCheckedChange={v=>setValue('requires_machine',v,{shouldDirty:true})} /></div>
            <div className="flex items-center justify-between"><Label>Requiere Material</Label><Switch checked={watch('requires_material')} onCheckedChange={v=>setValue('requires_material',v,{shouldDirty:true})} /></div>
            <div className="flex items-center justify-between"><Label>Activo</Label><Switch checked={watch('is_active')} onCheckedChange={v=>setValue('is_active',v,{shouldDirty:true})} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isSubmitting||!isValid||!isDirty}>{isSubmitting?'Guardando...':isEdit?'Guardar':'Crear'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}